import { Component, Input, ElementRef, ViewChild, Output, EventEmitter } from '@angular/core';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { ControlValueAccessor, UntypedFormControl } from '@angular/forms';
import {
  MatLegacyChipInput as MatChipInput,
  MatLegacyChipInputEvent as MatChipInputEvent,
} from '@angular/material/legacy-chips';
import {
  MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent,
  MatLegacyAutocompleteActivatedEvent as MatAutocompleteActivatedEvent,
} from '@angular/material/legacy-autocomplete';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';
import { difference, filter, forEachRight } from 'lodash-es';
import { provideValueAccessor } from 'studio-components/shared/value-accessor.utils';

@Component({
  selector: 'studio-form-chip-list',
  templateUrl: './form-chip-list.component.html',
  styleUrls: ['./form-chip-list.component.scss'],
  providers: [provideValueAccessor(() => FormChipListComponent)],
})
export class FormChipListComponent implements ControlValueAccessor {
  @Output() valueSaved = new EventEmitter<string[]>();
  @Output() valueAdded = new EventEmitter<string>();
  @Output() valueRemoved = new EventEmitter<string>();
  @Output() valueCreated = new EventEmitter<string>();
  @Input() public set values(values: string[]) {
    this.valuesSubject.next(Array.isArray(values) ? [...values] : []);
  }
  @Input() public uniqueId = '';
  @Input() public placeholder = '';
  @Input() public removeDuplicates = false;
  @Input() public autocomplete = false;
  @Input() public set autocompleteOptions(value: string[]) {
    this.autocompleteOptionsSubject.next(value);
  }
  public get autocompleteOptions() {
    return this.autocompleteOptionsSubject.value;
  }
  @Input() public singleChip = false;

  readonly separatorKeyCodes: number[] = [ENTER, COMMA];
  inputCtrl = new UntypedFormControl();
  activeOptions$: Observable<string[]>;
  filteredOptions$: Observable<string[]>;
  private ignoreAdd = false;
  private valuesSubject = new BehaviorSubject<string[]>([]);
  public get values() {
    return this.valuesSubject.value;
  }
  private autocompleteOptionsSubject = new BehaviorSubject<string[]>([]);

  @ViewChild('input', { static: false }) inputElement: ElementRef<HTMLInputElement>;

  constructor() {
    this.setupActiveOptions();
    this.setupAutocomplete();
  }

  private addValue(value: string) {
    const values = [...this.values, value];
    this.valuesSubject.next(values);
  }

  private removeValue(value: string) {
    const i = this.values.indexOf(value);
    const values = [...this.values];
    values.splice(i, 1);
    this.valuesSubject.next(values);
  }

  add(event: MatChipInputEvent): void {
    // if this call to add was initiated via a keyboard selection in the drop down,
    // ignore it as it will have the user entered partial value (ie: 'F')
    // optionSelected will subsequently be called and handle the add with the
    // full value selected from the drop down(ie: 'Foo')
    //
    // refer to related Material Github issue (none of the workarounds are reliable):
    // https://github.com/angular/components/issues/19805
    if (!this.ignoreAdd) {
      const { input, value } = event;
      this.addChip(value);
      if (input) {
        input.value = '';
      }
    }
  }

  optionSelected(event: MatAutocompleteSelectedEvent): void {
    this.addChip(event.option.viewValue);
    this.inputElement.nativeElement.value = '';
    this.inputCtrl.setValue(null);
  }

  optionActivated(event: MatAutocompleteActivatedEvent) {
    // optionActivated is fired when an option is selected in the drop down *via the keyboard*
    // it is subsequently fired with null once the selected option has been added as a chip
    // in between these two events, we want to ignore the related call to add()
    this.ignoreAdd = !!event.option;
  }

  remove(value: string): void {
    const index = this.values.indexOf(value);

    if (index >= 0) {
      this.removeValue(value);
      this.valueRemoved.emit(value);
      this.save();
    }
  }

  save() {
    this.valueSaved.emit(this.values);
    this.onChange(this.values);
  }

  addChip(value: string) {
    const trimmedValue = (value || '').trim();

    if (trimmedValue) {
      if (this.singleChip && this.values.length) {
        forEachRight(this.values, (chip) => this.remove(chip));
      }

      if (this.removeDuplicates && this.values.includes(trimmedValue)) {
        this.removeValue(trimmedValue);
      }

      this.addValue(trimmedValue);
      this.valueAdded.emit(trimmedValue);
      this.save();

      if (!this.autocompleteOptions.includes(trimmedValue)) {
        this.valueCreated.emit(trimmedValue);
      }
    }
  }

  // when chip values or options change, filter autocomplete list according to remove duplicates & already used values
  private setupActiveOptions() {
    this.activeOptions$ = combineLatest([this.valuesSubject, this.autocompleteOptionsSubject]).pipe(
      map(([values, options]) => this.getActiveOptions(values, options)),
    );
  }

  // when input changes (user is typing), filter list based on that input
  private setupAutocomplete() {
    this.filteredOptions$ = this.inputCtrl.valueChanges.pipe(
      startWith(<string>null),
      switchMap((inputText: string | null) =>
        this.activeOptions$.pipe(
          map((activeOptions) => this.filterOptionsList(activeOptions, inputText)),
        ),
      ),
    );
  }

  private getActiveOptions(values: string[], autocompleteOptions: string[]) {
    return this.removeDuplicates ? difference(autocompleteOptions, values) : autocompleteOptions;
  }

  private filterOptionsList(options: string[], filterText: string) {
    return filterText
      ? filter(options, (chip) => chip.toLowerCase().startsWith(filterText.toLowerCase()))
      : options.slice();
  }

  public onTouch(event: FocusEvent) {
    this.addOnBlur(event);
    this.onTouched();
  }

  // require custom on blur so that clicking an option in the drop down results in an add
  // can't use the inbuilt matChipInputAddOnBlur as it results in the issue where both
  // 'F','Foo' are added when 'F' is type and 'Foo' is clicked
  // this workaround is taken from https://github.com/angular/components/issues/19279#issuecomment-627263513
  addOnBlur(event: FocusEvent) {
    const target: HTMLElement = event.relatedTarget as HTMLElement;
    if (!target || target.tagName !== 'MAT-OPTION') {
      const matChipEvent: MatChipInputEvent = {
        input: this.inputElement.nativeElement,
        value: this.inputElement.nativeElement.value,
        chipInput: new MatChipInput(this.inputElement, { separatorKeyCodes: [] }),
      };
      this.add(matChipEvent);
    }
  }

  writeValue(values: string[]) {
    this.values = values;
  }

  onChange = (_: any) => {};
  onTouched = () => {};

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
}
